import React, { useEffect, useRef, useState } from "react"
import _isEmpty from "lodash/isEmpty"

import { appendPresetToUrl, getShortenedUrl } from "@/utils/helper"
import ProductSocialShare from "@/components/Shared/ProductSocialShare/v1/ProductSocialShare"
import { createContextLogger } from "@/utils/logger/client"

const ProductBreadcrumb = props => {
  const {
    staticTexts: {
      ariaLabel: { breadcrumb = "", page = "", print = "", share = "" } = {},
      home = "",
      serviceParts = "",
      priceFootnoteTitle = "",
      priceFootnote = "",
      colorFootnoteTitle = "",
      colorFootnote = "",
    },
    productCategory = "",
    name = "",
    homePagePath = "",
    plpServletPath = [],
    config = {},
    productAnalyticsData = "",
    productData = {},
    swatchUrl = "",
    imagesData = [],
    width = 0,
    presetConfigObject = {},
    isBundleProduct = "",
  } = props || {}

  const [url, setUrl] = useState("")
  const [plpPagePath, setPlpPagePath] = useState("")
  const [serviceLandingPath, setServiceLandingPath] = useState("")
  const [showSocialLink, setShowSocialLink] = useState(false)
  const shareBtn = useRef(null)
  const [localCategory, setLocalCategory] = useState(
    productData?.productLocalCategoryBreadCrumb_s
  )

  const logger = createContextLogger("pdp:breadcrumb")

  useEffect(() => {
    if (!_isEmpty(config)) {
      const { general = {} } = config
      getShortenedUrl(general.plpPagePath ?? "/")
        .then(res => setPlpPagePath(res))
        .catch(error => {
          logger.error("Failed to get shortened PLP page path", {
            error,
            path: general.plpPagePath,
          })
        })
      getShortenedUrl(general.servicePartsLandingPath ?? "/")
        .then(res => setServiceLandingPath(res))
        .catch(error => {
          logger.error("Failed to get shortened service parts landing path", {
            error,
            path: general.servicePartsLandingPath,
          })
        })
    }
  }, [config, plpServletPath])
  useEffect(() => {
    if (productData?.productLocalCategoryBreadCrumb_s) {
      setLocalCategory(productData?.productLocalCategoryBreadCrumb_s)
    }
  }, [productData])
  useEffect(() => {
    if (
      plpServletPath &&
      productCategory &&
      (plpPagePath || serviceLandingPath)
    ) {
      const path = plpServletPath?.find(i =>
        i.categories.includes(productCategory)
      )

      let plpUrl
      if (productCategory) {
        if (path) {
          plpUrl = `${path.url}.html`
        } else if (productCategory === "Service Parts") {
          plpUrl = `${serviceLandingPath}.html`
        } else {
          plpUrl = `${plpPagePath}/${productCategory
            .replace(/ +/g, "-")
            .toLowerCase()}.html`
        }
      } else {
        plpUrl = `${serviceLandingPath}.html`
      }

      getShortenedUrl(plpUrl)
        .then(url => setUrl(url))
        .catch(error => {
          logger.error("Failed to get shortened PLP URL", { error, plpUrl })
        })
    }
  }, [plpPagePath, serviceLandingPath, productCategory])

  // Hide social share options when user clicks outide the options
  useEffect(() => {
    window.addEventListener("click", handleClickOutside)
    return () => window.removeEventListener("click", handleClickOutside)
  }, [])

  const handleClickOutside = e => {
    const { target } = e
    if (!shareBtn.current.contains(target)) {
      setShowSocialLink(false)
    }
  }

  const handleShareClick = () => {
    setShowSocialLink(prevProp => !prevProp)

    const kohlerDatalayer = window.kohler.datalayer
    kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
      page: page,
      eventMetaData: {
        experience: "pdp",
        eventType: "ShareBtn",
        params: {
          stringProductInfo: productAnalyticsData?.stringProductInfo,
        },
      },
    })
  }
  const addAnalytics = (internalLinkName = "", link = "") => {
    const page = window.pageObj || {}

    const kohlerDatalayer = window.kohler.datalayer
    kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
      page: page,
      eventMetaData: {
        experience: "pdp",
        eventType: "BreadcrumbEvent",
        params: {
          internalLinkName,
          link,
        },
      },
    })
  }

  const printPDP = () => {
    const printNoteLen = document.getElementsByClassName(
      "product-detail-page__print-note"
    ).length
    if (printNoteLen === 0) {
      const printNote = `<div class='product-detail-page__print-note'><div class='container'><div class='row'><div class='col-12'><h6>${priceFootnoteTitle}</h6><p>${priceFootnote}</p><h6>${colorFootnoteTitle}</h6> <p>${colorFootnote}</p></div></div></div></div>`
      const printNoteDiv = document.createElement("div")
      printNoteDiv.innerHTML = printNote
      document.body.appendChild(printNoteDiv)
    }
    setTimeout(() => {
      window.print()
    }, 900)

    const kohlerDatalayer = window.kohler.datalayer
    kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
      page: page,
      eventMetaData: {
        experience: "pdp",
        eventType: "genericEvent",
        params: {
          type: "print",
          needsJSON: true,
        },
      },
    })
  }
  if (productData) {
    return (
      <div className="header-section kf-react-plp-container">
        <div className="product-detail-page__breadcrumb">
          <nav aria-label={breadcrumb}>
            <ol data-testid="bcm-list">
              <li>
                <a
                  aria-label={`${home} ${page}`}
                  className="product-detail-page__breadcrumb-link"
                  onClick={() => addAnalytics(home, homePagePath)}
                  href={homePagePath}
                >
                  {` ${home}`}
                </a>
                {" / "}
              </li>
              <li>
                <a
                  aria-label={
                    productCategory
                      ? `${productCategory} ${page}`
                      : `${serviceParts} ${page}`
                  }
                  className="product-detail-page__breadcrumb-link"
                  onClick={() =>
                    addAnalytics(`${home} > ${productCategory}`, url)
                  }
                  href={url}
                >
                  {localCategory ? localCategory : serviceParts}
                </a>
              </li>
              <li className="adaopacity">
                {" / "}
                <a
                  aria-label={
                    productCategory
                      ? `${name} ${page}`
                      : `${serviceParts} ${page}`
                  }
                  aria-current={page}
                  className="product-detail-page__breadcrumb-link"
                  onClick={() =>
                    addAnalytics(`${home} > ${productCategory} > ${name}`, "#")
                  }
                  href="#"
                >
                  {productCategory ? name : serviceParts}
                </a>
              </li>
            </ol>
          </nav>
        </div>
        <div className="product-detail-page__print-share-container">
          <div role="list" className="print-and-share">
            <span role="listitem">
              <button
                tabIndex="0"
                aria-label={print}
                onClick={() => printPDP()}
              >
                {" "}
                <img
                  src="/icons/print.svg"
                  alt=""
                  role="presentation"
                  width="17"
                  height="17"
                />
              </button>
            </span>
            <span role="listitem">
              <button
                tabIndex="0"
                aria-label={share}
                className="share-button"
                ref={shareBtn}
                onClick={handleShareClick}
                aria-expanded={showSocialLink}
                aria-controls="print-and-share"
              >
                <img
                  src="/icons/share.svg"
                  alt=""
                  role="presentation"
                  width="17"
                  height="17"
                />
              </button>
            </span>
          </div>
          <ProductSocialShare
            disableInstagram
            showModal={showSocialLink}
            shareBtn={shareBtn}
            id="print-and-share"
            onClose={() => shareBtn?.current.focus()}
            asset={{
              description: productData?.ProductDescriptionProductShort_s ?? "",
              url: appendPresetToUrl(
                swatchUrl,
                imagesData[0]?.assetId,
                width,
                presetConfigObject,
                true
              ),
            }}
            analyticData={{
              eventAction: "pdp:product details:share",
              eventName: "pdp:product details:share",
              internalLinkPosition: `pdp${isBundleProduct ? ":bundle" : ""}`,
              internalLinkType: "pdp:share",
              internalLinkZoneName: "pdp:product details",
              custom: productAnalyticsData.stringProductInfo,
            }}
            isBundleProduct={isBundleProduct}
          />
        </div>
      </div>
    )
  }
}

export default ProductBreadcrumb
